import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  BookOpen, Globe, Layers, Lightbulb, Sparkles, Network,
  ChevronDown, ChevronUp, ClipboardCheck, Check, Lock, BarChart3,
  Clock, TrendingUp, Brain, Palette, Code, Download, ArrowRight
} from 'lucide-react';
import HamburgerMenu from './HamburgerMenu';
import { flatlandTrapTwee } from '../data/flatlandTrap';
import { compileFlatlandStory } from '../utils/flatlandCompiler';
import SEO, { generateFAQSchema } from './SEO';
import StoryNavSml from './StoryNavSml';
import { beginGoogleAdminLogin } from '../services/adminAuthService';
import AccountStatusButton from './AccountStatusButton';

// ─── FAQ content (unchanged — preserved for SEO) ────────────────────────────
const FAQ_DATA = [
  {
    question: "What website builders does TweePorter work with?",
    answer: "TweePorter works with GoDaddy, Wix, Squarespace, Shopify, Webflow, WordPress (HTML blocks), and any website builder that accepts custom HTML. The output is script-free, so it bypasses restrictions that normally block interactive content."
  },
  {
    question: "Do I need coding experience to use TweePorter?",
    answer: "No coding experience required! If you can use Twine (a free visual tool for creating interactive stories), you can use TweePorter. Simply paste your Twee code and get clean HTML output. We also offer a Visual Story Planner with AI assistance for those who prefer not to write code at all."
  },
  {
    question: "Is TweePorter free to use?",
    answer: "Yes. Free Preview lets you build locally, save your own Twee source, and test up to 12 reachable passages. Export-ready output uses Welcome Pack credits or DIY access."
  },
  {
    question: "What is Twee/SugarCube format?",
    answer: "Twee is a plain-text format for writing interactive fiction, commonly used with Twine. SugarCube is a popular story format that adds features like variables, macros, and styling. TweePorter is optimized for SugarCube-style Twee code."
  },
  {
    question: "Can I use variables and conditional logic in my stories?",
    answer: "Yes! TweePorter supports SugarCube variables and basic conditional logic. You can create dynamic stories that change based on reader choices, track inventory items, or show different content based on previous decisions."
  },
  {
    question: "How do I embed the compiled story on my website?",
    answer: "When you are ready to export, generate the output with Welcome Pack credits or DIY access, then paste the generated HTML into any HTML block or custom code section on your website builder."
  },
  {
    question: "What's the difference between the Compiler and Visual Story Planner?",
    answer: "The Compiler is for users who already have Twee code (from Twine or written manually). The Visual Story Planner is a drag-and-drop interface with AI assistance that helps you create stories from scratch without writing code."
  },
  {
    question: "Can I customize the look of my interactive stories?",
    answer: "Absolutely! TweePorter offers multiple layout options, color customization for text, links, backgrounds, and buttons. You can match your story's appearance to your website's branding."
  },
  {
    question: "Are there limits on story length or complexity?",
    answer: "There are no strict limits, but very large stories (hundreds of passages) may result in larger HTML files. For optimal performance, we recommend keeping stories under 100 passages, though the system can handle more."
  },
  {
    question: "What is the Customer Journey Wizard?",
    answer: "The Customer Journey Wizard is a specialized tool that helps businesses create interactive customer journey maps. It guides you through defining problems, personas, features, and story arcs, then generates an interactive story that demonstrates your product's value proposition."
  }
];

// ─── Reusable accordion header ───────────────────────────────────────────────
const AccordionToggle = ({ title, expanded, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testId}
    aria-expanded={expanded}
    className="w-full flex items-center justify-between px-6 py-5 bg-white hover:bg-slate-50 transition-colors rounded-xl border border-slate-200 text-left"
  >
    <span className="text-xl font-semibold text-slate-900">{title}</span>
    {expanded
      ? <ChevronUp className="h-5 w-5 text-indigo-600 flex-shrink-0" />
      : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
  </button>
);

// ─── Accordion body wrapper (CSS-only, SEO-safe) ─────────────────────────────
const AccordionBody = ({ expanded, children }) => (
  <div
    className={`transition-all duration-300 ease-in-out overflow-hidden ${
      expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
    }`}
    aria-hidden={!expanded}
  >
    <div className="pt-3">{children}</div>
  </div>
);

const engagementStats = [
  { value: '87%', label: 'of marketers say interactive content grabs attention better', source: 'Content Marketing Institute', url: 'https://contentmarketinginstitute.com/wp-content/uploads/2017/06/IonInteractive_Symphony_Final.pdf', Icon: BarChart3 },
  { value: '4–5 min', label: 'longer time-on-page than static content', source: 'SalesHub', url: 'https://www.saleshub.ca/these-interactive-content-platforms-are-transforming-digital-marketing-results/', Icon: Clock },
  { value: '40–50%', label: 'conversion rates reported for quizzes', source: 'Outgrow', url: 'https://outgrow.co/why-interactive/', Icon: TrendingUp },
  { value: '73%', label: 'say interactive content improves message retention', source: 'Content Marketing Institute', url: 'https://contentmarketinginstitute.com/wp-content/uploads/2017/06/IonInteractive_Symphony_Final.pdf', Icon: Brain },
];

const workflowSteps = [
  { title: 'Choose', label: 'Start with an example or a blank story', detail: 'Browse a ready-made story, or bring your own idea and create the first passage.', cta: 'Browse examples', path: '/sample-gallery', Icon: BookOpen },
  { title: 'Shape', label: 'Design the journey and choices', detail: 'Write in plain Twee syntax, then organize reader choices into a clear path.', cta: 'Open workspace', path: '/free', Icon: Palette },
  { title: 'Preview', label: 'Test the first 12 passages free', detail: 'Use the local preview to test the story flow before you commit to output.', cta: 'Try free preview', path: '/free', Icon: Code },
  { title: 'Export', label: 'Generate output when you are ready', detail: 'Register for Welcome Pack downloads or choose DIY for advanced export tools.', cta: 'See plans', path: '/pricing', Icon: Download },
];

const EngagementProof = () => (
  <section className="container mx-auto px-4 pb-12" data-testid="hp3-engagement-stats-section">
    <div className="max-w-4xl mx-auto border-y border-slate-100 py-8">
      <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest text-center mb-5">Why interactive stories earn attention</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {engagementStats.map(({ value, label, source, url, Icon }) => (
          <a key={value} href={url} target="_blank" rel="noreferrer" data-testid={`hp3-stat-${value.replace(/[^a-z0-9]/gi, '').toLowerCase()}`} className="border border-slate-200 bg-white p-4 text-center transition-shadow hover:shadow-sm rounded-lg">
            <span className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50"><Icon className="h-4 w-4 text-indigo-600" /></span>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            <p className="mt-1 text-xs leading-snug text-slate-600">{label}</p>
            <p className="mt-2 text-[11px] font-medium text-indigo-600">Source: {source}</p>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = ({ activeStep, onStepChange, onNavigate }) => {
  const step = workflowSteps[activeStep];
  const StepIcon = step.Icon;
  return (
    <section className="container mx-auto px-4 pb-14" data-testid="hp3-how-it-works-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-7"><p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-2">A clear path from idea to output</p><h2 className="text-2xl md:text-3xl font-bold text-slate-900">How TweePorter Works</h2></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 border border-slate-200 rounded-lg overflow-hidden bg-white">
          {workflowSteps.map((item, index) => {
            const Icon = item.Icon;
            const selected = index === activeStep;
            return <button key={item.title} type="button" onClick={() => onStepChange(index)} data-testid={`hp3-workflow-step-${index + 1}`} aria-pressed={selected} className={`min-h-[112px] border-b sm:border-b-0 sm:border-r last:border-0 px-3 py-4 text-center transition-colors ${selected ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}><Icon className="mx-auto mb-2 h-5 w-5" /><span className="block text-xs font-semibold uppercase tracking-wide">Step {index + 1}</span><span className="mt-1 block text-sm font-semibold">{item.title}</span></button>;
          })}
        </div>
        <div className="border border-t-0 border-slate-200 bg-white p-6 md:p-8 rounded-b-lg" data-testid="hp3-workflow-detail">
          <div className="flex items-start gap-4"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-50"><StepIcon className="h-5 w-5 text-indigo-600" /></span><div><p className="text-xl font-semibold text-slate-900">{step.label}</p><p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{step.detail}</p><Button onClick={() => onNavigate(step.path)} data-testid="hp3-workflow-cta" className="mt-5 bg-indigo-600 hover:bg-indigo-700">{step.cta}<ArrowRight className="ml-2 h-4 w-4" /></Button></div></div>
        </div>
      </div>
    </section>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const Landing3 = () => {
  const navigate = useNavigate();
  const demoRef = useRef(null);

  const [compiledStoryHtml, setCompiledStoryHtml] = useState('');
  const [demoExpanded, setDemoExpanded]                   = useState(false);
  const [whatThisSolvesExpanded, setWhatThisSolvesExpanded] = useState(false);
  const [whyMattersExpanded, setWhyMattersExpanded]       = useState(false);
  const [whatYouCanCreateExpanded, setWhatYouCanCreateExpanded] = useState(false);
  const [howItWorksExpanded, setHowItWorksExpanded]       = useState(false);
  const [keyFeaturesExpanded, setKeyFeaturesExpanded]     = useState(false);
  const [whyPeopleUseExpanded, setWhyPeopleUseExpanded]   = useState(false);
  const [expandedFaqs, setExpandedFaqs]                   = useState({});
  const [activeWorkflowStep, setActiveWorkflowStep]       = useState(0);

  const faqSchema = generateFAQSchema(FAQ_DATA);

  // Compile Flatland story once
  useEffect(() => {
    const html = compileFlatlandStory(flatlandTrapTwee);
    setCompiledStoryHtml(html);
  }, []);

  // Execute Flatland story script when expanded
  useEffect(() => {
    if (compiledStoryHtml && demoExpanded) {
      const scriptMatch = compiledStoryHtml.match(/<script>([\s\S]*?)<\/script>/);
      if (scriptMatch) {
        try {
          const scriptFunc = new Function(scriptMatch[1]);
          scriptFunc();
        } catch (e) {
          console.error('Flatland script error:', e);
        }
      }
    }
  }, [compiledStoryHtml, demoExpanded]);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const toggleFaq = (index) => {
    setExpandedFaqs(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSeeInAction = () => {
    setDemoExpanded(true);
    setTimeout(() => {
      demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="TweePorter - Convert Twine Stories to Script-Free HTML | Works on Wix, GoDaddy, Squarespace"
        description="Turn Twine interactive stories into clean HTML that works inside GoDaddy, Wix, Squarespace, and Shopify—no scripts, plugins, or hosting required. Free online tool."
        canonicalUrl="/home3"
        structuredData={faqSchema}
      />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="container mx-auto px-4 py-6 flex items-center justify-between border-b border-slate-100">
        <div
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
        >
          <BookOpen className="h-7 w-7 text-indigo-600" />
          <div>
            <span className="text-xl font-bold text-slate-900">TweePorter.ca</span>
            <div className="flex items-center space-x-1 text-xs text-indigo-500 font-medium">
              <Sparkles className="h-3 w-3" />
              <span>Optimized for SugarCube</span>
            </div>
          </div>
        </div>
        <AccountStatusButton floating />
        <HamburgerMenu />
      </header>

      {/* ── Zone 1: Value Proposition + Entry Paths ────────────────────────── */}
      <section className="container mx-auto px-4 pt-16 pb-12 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-5 leading-tight tracking-tight">
            Bring Interactive Stories to Any Website Builder
          </h1>
          <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto">
            Paste Twee code. Get clean HTML that runs inside GoDaddy, Wix, Squarespace, or Shopify — no scripts, no plugins, no hosting required.
          </p>

          {/* Three entry paths — weight distinguishes user intent */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Button
              data-testid="hp3-start-building-btn"
              onClick={() => navigate('/free')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-7 py-3 h-auto rounded-full text-base font-semibold whitespace-nowrap"
            >
              Try the Compiler Story Builder
            </Button>
            <Button
              data-testid="hp3-see-in-action-btn"
              variant="outline"
              onClick={handleSeeInAction}
              className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-7 py-3 h-auto rounded-full text-base font-semibold whitespace-nowrap"
            >
              See an Interactive Story
            </Button>
          </div>
          {/* Third CTA: interactive interest map — same StoryNavSml used in PageHeader */}
          <StoryNavSml />
        </div>
      </section>

      <EngagementProof />
      <HowItWorks activeStep={activeWorkflowStep} onStepChange={setActiveWorkflowStep} onNavigate={navigate} />

      {/* ── Live Demo (collapsed by default) ──────────────────────────────── */}
      <section ref={demoRef} className="container mx-auto px-4 pb-14">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            data-testid="hp3-demo-toggle"
            onClick={() => setDemoExpanded(!demoExpanded)}
            className={`w-full flex items-center justify-between px-6 py-4 border rounded-xl transition-colors ${
              demoExpanded ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-3">
              {!demoExpanded && <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">Sample output</span>}
              <span className="font-semibold">See it in action — click here</span>
            </div>
            {demoExpanded ? <ChevronUp className="h-5 w-5 text-white flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
          </button>
          <AccordionBody expanded={demoExpanded}>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl overflow-hidden">
              <div className="bg-indigo-100 border-b border-indigo-200 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div><p className="font-semibold text-indigo-900 text-sm">The Flatland Trap: A Skeptic's Journey</p><p className="text-xs text-indigo-600 mt-0.5 italic">See how interactive content embeds directly — no iframe needed</p></div>
                <div className="flex items-center gap-3 text-sm"><button onClick={() => window.flatlandGoBack && window.flatlandGoBack()} data-testid="hp3-demo-back-button" className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 transition-colors text-xs font-medium">← Back</button><button onClick={() => window.flatlandReset && window.flatlandReset()} data-testid="hp3-demo-reset-button" className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 transition-colors text-xs font-medium">Reset</button></div>
              </div>
              <div className="px-6 py-8" dangerouslySetInnerHTML={{ __html: compiledStoryHtml }} />
            </div>
          </AccordionBody>
        </div>
      </section>

      {/* ── Zone 2: Outcome Examples ───────────────────────────────────────── */}
      <section className="container mx-auto px-4 pb-14">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest text-center mb-8">
            What you can make with TweePorter
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            <button
              type="button"
              data-testid="hp3-outcome-card-product"
              onClick={() => navigate('/examples#business-examples')}
              className="bg-white border border-slate-200 rounded-xl p-6 text-left hover:border-indigo-300 hover:shadow-sm transition-all group"
            >
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Product embed</p>
              <p className="text-slate-800 font-medium leading-snug group-hover:text-indigo-700 transition-colors">A product comparison walkthrough living inside a Squarespace page</p>
              <p className="text-xs text-indigo-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">See business examples →</p>
            </button>
            <button
              type="button"
              data-testid="hp3-outcome-card-training"
              onClick={() => navigate('/examples#training-examples')}
              className="bg-white border border-slate-200 rounded-xl p-6 text-left hover:border-indigo-300 hover:shadow-sm transition-all group"
            >
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Training scenario</p>
              <p className="text-slate-800 font-medium leading-snug group-hover:text-indigo-700 transition-colors">A branching training module embedded in a GoDaddy blog post</p>
              <p className="text-xs text-indigo-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">See training examples →</p>
            </button>
            <button
              type="button"
              data-testid="hp3-outcome-card-journey"
              onClick={() => navigate('/examples#personal-examples')}
              className="bg-white border border-slate-200 rounded-xl p-6 text-left hover:border-indigo-300 hover:shadow-sm transition-all group"
            >
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Customer journey</p>
              <p className="text-slate-800 font-medium leading-snug group-hover:text-indigo-700 transition-colors">A client journey map running in WordPress without any plugin</p>
              <p className="text-xs text-indigo-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">See personal examples →</p>
            </button>
          </div>
        </div>
      </section>

      {/* ── Zone 4: What This Solves ──────────────────────────────────────── */}
      <section className="container mx-auto px-4 pb-6">
        <div className="max-w-4xl mx-auto space-y-2">
          <AccordionToggle
            title="What This Solves"
            expanded={whatThisSolvesExpanded}
            onToggle={() => setWhatThisSolvesExpanded(!whatThisSolvesExpanded)}
            testId="hp3-what-solves-toggle"
          />
          <AccordionBody expanded={whatThisSolvesExpanded}>
            <div className="bg-white border border-slate-200 rounded-xl px-6 py-5 space-y-5">
              <p className="text-slate-600 leading-relaxed">
                Create interactive stories that work on any website. Three tiers to match your needs:{' '}
                <strong>DIY</strong> for hobbyists, <strong>PRO</strong> for serious creators, and{' '}
                <strong>Agency</strong> for teams building client experiences.
              </p>
              <div className="space-y-4 pt-2 border-t border-slate-100">
                <div className="flex gap-4">
                  <span className="px-2.5 py-1 bg-slate-800 text-white text-xs font-bold rounded-full h-fit mt-0.5">DIY</span>
                  <div>
                    <p className="text-slate-800 font-medium">Interactive Story DIY</p>
                    <p className="text-slate-500 text-sm mt-1">Browse sample stories, learn the format, and use our Easy Story Builder (Compiler) to create script-free HTML that works on GoDaddy, Wix, Squarespace, and more.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="px-2.5 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full h-fit mt-0.5">PRO</span>
                  <div>
                    <p className="text-slate-800 font-medium">Professional Tools</p>
                    <p className="text-slate-500 text-sm mt-1">Visual Editor for node-based story building, Visual Story Planner for complex narratives, Shared Story sessions for live multi-user experiences, plus premium export formats.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="px-2.5 py-1 bg-slate-600 text-white text-xs font-bold rounded-full h-fit mt-0.5">AGENCY</span>
                  <div>
                    <p className="text-slate-800 font-medium">Agency Suite</p>
                    <p className="text-slate-500 text-sm mt-1">Everything in PRO, plus Sitemap to PathNav for converting websites into interactive navigation maps, Customer Journey Wizard for client projects, and upcoming enterprise features.</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm italic pt-2 border-t border-slate-100">
                  All tiers produce clean, script-free HTML — no external hosting or dependencies required.
                </p>
              </div>
            </div>
          </AccordionBody>
        </div>
      </section>

      {/* ── New Tool Spotlight: Comparison Checklist ─────────────────────── */}
      <section className="container mx-auto px-4 pb-6">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            data-testid="hp3-checklist-promo"
            onClick={() => navigate('/checklist')}
            className="w-full bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-left hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
          >
            <div className="p-2.5 bg-indigo-50 rounded-lg flex-shrink-0">
              <ClipboardCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <span className="flex-1">
              <span className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">New</span>
                <span className="text-sm font-semibold text-slate-800">DIY Comparison Checklist Builder</span>
              </span>
              <span className="block text-sm text-slate-500">
                Vendors: build a weighted comparison checklist. Buyers: step through features, score competitors, export to CSV.
              </span>
            </span>
            <span className="text-indigo-600 font-semibold text-sm flex-shrink-0">Try it →</span>
          </button>
        </div>
      </section>

      {/* ── Why This Matters ─────────────────────────────────────────────── */}
      <section className="bg-slate-50">
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-4xl mx-auto space-y-2">
            <AccordionToggle
              title="Why This Matters"
              expanded={whyMattersExpanded}
              onToggle={() => setWhyMattersExpanded(!whyMattersExpanded)}
              testId="hp3-why-matters-toggle"
            />
            <AccordionBody expanded={whyMattersExpanded}>
              <div className="bg-white border border-slate-200 rounded-xl px-6 py-5 space-y-3">
                <p className="text-slate-600 leading-relaxed">
                  Interactive stories engage visitors more deeply. With paths and decisions, your visitors don&apos;t just read content — they experience it.
                </p>
                <h3 className="text-base font-semibold text-slate-800 pt-2">Engagement Through Interactivity</h3>
                <p className="text-slate-600 leading-relaxed">
                  Traditional website builders block scripts, making it nearly impossible to embed interactive stories. TweePorter.ca solves this by restructuring your narrative into simple, safe HTML that always works.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Whether you&apos;re creating product tours, training content, or creative fiction — interactive paths keep visitors engaged longer.
                </p>
              </div>
            </AccordionBody>
          </div>
        </div>
      </section>

      {/* ── What You Can Create ──────────────────────────────────────────── */}
      <section id="what-you-can-create" className="container mx-auto px-4 py-10">
        <div className="max-w-4xl mx-auto space-y-2">
          <AccordionToggle
            title="What You Can Create"
            expanded={whatYouCanCreateExpanded}
            onToggle={() => setWhatYouCanCreateExpanded(!whatYouCanCreateExpanded)}
            testId="hp3-what-create-toggle"
          />
          <AccordionBody expanded={whatYouCanCreateExpanded}>
            <div className="grid md:grid-cols-3 gap-5">
              <div id="personal-examples" className="bg-white border border-slate-200 rounded-xl p-6 scroll-mt-20">
                <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center mb-4">
                  <BookOpen className="h-5 w-5 text-indigo-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Personal Stories & Creative Projects</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Share interactive adventures, fictional narratives, family stories, or community projects. Let readers make choices and explore multiple paths just like a classic "choose your own adventure."
                </p>
                <div className="pt-3 border-t border-slate-100 space-y-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Try examples</p>
                  <a href="/examples/personal-the-missed-call" className="block text-sm text-indigo-600 hover:text-indigo-700 hover:underline">→ The Missed Call</a>
                  <a href="/examples/personal-library-that-wasnt-there" className="block text-sm text-indigo-600 hover:text-indigo-700 hover:underline">→ The Library That Wasn't There</a>
                </div>
              </div>

              <div id="business-examples" className="bg-white border border-slate-200 rounded-xl p-6 scroll-mt-20">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="h-5 w-5 text-slate-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Business Case Studies That Convert</h3>
                <p className="text-slate-500 text-sm mb-3">
                  Show prospects why your solution matters by letting them make decisions in the story. Demonstrate how the right path leads to better outcomes, cost savings, or avoided risks.
                </p>
                <p className="text-slate-600 text-sm mb-4">
                  <strong>Businesses use interactive, decision-based stories to demonstrate ROI, train staff, and show how choosing the right path leads to measurable financial outcomes.</strong>
                </p>
                <div className="pt-3 border-t border-slate-100 space-y-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Try examples</p>
                  <a href="/examples/business-cloud-migration-crossroads" className="block text-sm text-indigo-600 hover:text-indigo-700 hover:underline">→ Cloud Migration Crossroads</a>
                  <a href="/examples/business-the-free-discount" className="block text-sm text-indigo-600 hover:text-indigo-700 hover:underline">→ The "Free" Discount</a>
                </div>
              </div>

              <div id="training-examples" className="bg-white border border-slate-200 rounded-xl p-6 scroll-mt-20">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center mb-4">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Training, Education, and Onboarding</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Create branching scenarios where learners make decisions, see consequences instantly, and explore alternative outcomes — no LMS required. Just paste the HTML anywhere.
                </p>
                <div className="pt-3 border-t border-slate-100 space-y-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Try examples</p>
                  <a href="/examples/training-first-day-on-the-factory-floor" className="block text-sm text-indigo-600 hover:text-indigo-700 hover:underline">→ First Day on the Factory Floor</a>
                  <a href="/examples/training-inbox-training-suspicious-invoice" className="block text-sm text-indigo-600 hover:text-indigo-700 hover:underline">→ Inbox Training: Suspicious Invoice</a>
                </div>
              </div>
            </div>
          </AccordionBody>
        </div>
      </section>

      {/* ── Compatibility strip ──────────────────────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4 py-5">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-800">Compatible with:</span>{' '}
              GoDaddy, Wix, Squarespace, Shopify, Webflow, WordPress (HTML blocks), and more.
            </p>
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────── */}
      <section className="container mx-auto px-4 py-10">
        <div className="max-w-4xl mx-auto space-y-2">
          <AccordionToggle
            title="How It Works"
            expanded={howItWorksExpanded}
            onToggle={() => setHowItWorksExpanded(!howItWorksExpanded)}
            testId="hp3-how-works-toggle"
          />
          <AccordionBody expanded={howItWorksExpanded}>
            <div className="bg-white border border-slate-200 rounded-xl px-6 py-6 space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-9 h-9 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Paste your interactive story code into the editor</h3>
                  <p className="text-slate-500 text-sm">Use our Visual Story Planner, or create your story with external tools — works with standard story formats.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-9 h-9 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">TweePorter converts it into clean, script-free HTML</h3>
                  <p className="text-slate-500 text-sm">No JavaScript required — pure HTML that any website builder accepts.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-9 h-9 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Paste the HTML into your website</h3>
                  <p className="text-slate-500 text-sm">Works on GoDaddy, Wix, Squarespace, Shopify, or anywhere that allows custom HTML.</p>
                </div>
              </div>
              <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500">
                  <Lock className="inline h-4 w-4 mr-1 text-slate-400" />
                  <strong className="text-slate-700">Privacy:</strong> Exported story HTML is delivered securely. We never sell or share your information.
                </p>
              </div>
            </div>
          </AccordionBody>
        </div>
      </section>

      {/* ── Key Features ─────────────────────────────────────────────────── */}
      <section className="bg-slate-50">
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-4xl mx-auto space-y-2">
            <AccordionToggle
              title="Key Features"
              expanded={keyFeaturesExpanded}
              onToggle={() => setKeyFeaturesExpanded(!keyFeaturesExpanded)}
              testId="hp3-key-features-toggle"
            />
            <AccordionBody expanded={keyFeaturesExpanded}>
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <p className="text-center text-slate-500 text-sm mb-6">Powerful tools that make interactive storytelling accessible to everyone</p>
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Globe className="h-4 w-4 text-indigo-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm pt-2">7 Export Formats</h3>
                    </div>
                    <p className="text-slate-500 text-sm">
                      Export your story in multiple formats: WordPress blocks, CMS packs, standalone HTML/CSS, minified versions, iframe embeds, and email-safe previews. One story, unlimited publishing options.
                    </p>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl p-5 relative">
                    <span className="absolute -top-2 right-4 text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full font-bold">NEW</span>
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Layers className="h-4 w-4 text-indigo-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm pt-2">Smart Passage Splitting</h3>
                    </div>
                    <p className="text-slate-500 text-sm mb-2">
                      Automatically splits long story passages into mobile-friendly chunks with "Continue..." navigation. Solves the hidden content problem on restrictive CMS platforms.
                    </p>
                    <p className="text-indigo-600 text-xs font-medium">Perfect for mobile readers and platforms with limited CSS control</p>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Network className="h-4 w-4 text-slate-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm pt-2">Visual Story Planner</h3>
                    </div>
                    <p className="text-slate-500 text-sm">
                      Plan complex branching narratives with our visual tree interface. Organize chapters, define story vision, and use AI assistance to generate content.
                    </p>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Sparkles className="h-4 w-4 text-amber-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm pt-2">AI Story Generator Guide</h3>
                    </div>
                    <p className="text-slate-500 text-sm">
                      Get perfect story syntax from ChatGPT or Claude. Copy our pre-written prompting instructions to ensure your AI generates properly formatted, compiler-ready story code every time.
                    </p>
                  </div>
                </div>
              </div>
            </AccordionBody>
          </div>
        </div>
      </section>

      {/* ── Why People Use TweePorter ─────────────────────────────────────── */}
      <section className="container mx-auto px-4 py-10">
        <div className="max-w-4xl mx-auto space-y-2">
          <AccordionToggle
            title="Why People Use TweePorter.ca"
            expanded={whyPeopleUseExpanded}
            onToggle={() => setWhyPeopleUseExpanded(!whyPeopleUseExpanded)}
            testId="hp3-why-use-toggle"
          />
          <AccordionBody expanded={whyPeopleUseExpanded}>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Works on platforms that normally block interactive stories",
                  "Creates SEO-friendly, lightweight pages",
                  "Preserves the original storytelling structure and choices",
                  "Useful for hobbyists and professionals alike",
                  "Auto-splits long passages for mobile-friendly reading"
                ].map((reason, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm leading-relaxed">{reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </AccordionBody>
        </div>
      </section>

      {/* ── Who This Is For ──────────────────────────────────────────────── */}
      <section className="bg-slate-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Who This Is For</h2>
            <div className="space-y-3">
              {[
                "Story creators who want interactive narratives on website builders that block scripts",
                "Small businesses presenting interactive case studies",
                "Consultants demonstrating decision-based outcomes",
                "Educators building branching scenarios",
                "Anyone wanting dynamic, interactive website content"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-2.5 flex-shrink-0" />
                  <p className="text-slate-600">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust / System State strip ────────────────────────────────────── */}
      <section className="border-y border-slate-200 bg-white">
        <div className="container mx-auto px-4 py-5">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-5">
              {[
                "Nothing stored on our servers without your permission",
                "Compilation runs in your browser",
                "Exported HTML needs no external hosting"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>
            <a href="#faq" className="text-sm text-slate-400 hover:text-indigo-600 transition-colors whitespace-nowrap">
              How it works →
            </a>
          </div>
        </div>
      </section>

      {/* ── CTA Section ──────────────────────────────────────────────────── */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto bg-indigo-600 rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Ready to Bring Your Interactive Stories to Life?</h2>
          <p className="text-indigo-200 mb-8">Create your first interactive story in minutes.</p>
          <Button
            size="lg"
            onClick={() => navigate('/free')}
            data-testid="hp3-bottom-cta-button"
            className="bg-white text-indigo-600 hover:bg-slate-100 font-semibold px-8 py-3 h-auto rounded-full"
          >
            Try the Compiler Now
          </Button>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="bg-slate-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100">
                <h2 className="text-xl font-bold text-slate-900 mb-1">Frequently Asked Questions</h2>
                <p className="text-sm text-slate-500">Common questions about TweePorter, interactive stories, and website embedding.</p>
              </div>

              <div>
                {FAQ_DATA.map((faq, index) => (
                  <div key={index} className="border-b border-slate-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleFaq(index)}
                      data-testid={`hp3-faq-toggle-${index + 1}`}
                      className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                      aria-expanded={expandedFaqs[index]}
                    >
                      <span className="font-medium text-slate-800 pr-4 text-sm">{faq.question}</span>
                      {expandedFaqs[index]
                        ? <ChevronUp className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                        : <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                    </button>
                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        expandedFaqs[index] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                      aria-hidden={!expandedFaqs[index]}
                    >
                      <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed">{faq.answer}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    const allExpanded = Object.keys(expandedFaqs).length === FAQ_DATA.length &&
                      Object.values(expandedFaqs).every(v => v);
                    const newState = {};
                    FAQ_DATA.forEach((_, i) => { newState[i] = !allExpanded; });
                    setExpandedFaqs(newState);
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  data-testid="hp3-faq-toggle-all"
                >
                  {Object.keys(expandedFaqs).length === FAQ_DATA.length &&
                    Object.values(expandedFaqs).every(v => v)
                    ? 'Collapse all'
                    : 'Expand all answers'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="container mx-auto px-4 py-12 border-t border-slate-200">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-900 mb-1">Interactive stories. Zero coding. Works anywhere.</p>
          <p className="text-sm text-slate-400">© 2025 TweePorter.ca Website. Built with care for storytellers.</p>
          <button type="button" onClick={beginGoogleAdminLogin} data-testid="hp3-admin-login-button" className="mt-5 text-xs text-slate-400 transition-colors hover:text-indigo-600">Administrator sign in</button>
        </div>
      </footer>
    </div>
  );
};

export default Landing3;
